import DOMPurify from 'dompurify';

/**
 * Utility functions for converting between ReactQuill HTML and Unity TextMeshPro (TMP) format
 *
 * The ProjectCell component uses ReactQuill for rich text editing on the web,
 * but needs to convert the content to Unity TextMeshPro format for 3D rendering.
 * These functions handle the bidirectional conversion between these formats.
 */

/**
 * Converts RGB color format to hex format
 * @param rgbString - RGB color string like 'rgb(230, 0, 0)' or 'rgba(230, 0, 0, 0.5)'
 * @returns Hex color string like '#E60000' or null if invalid
 */
const rgbToHex = (rgbString: string): string | null => {
  const rgbMatch = rgbString.match(
    /rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/
  );
  if (!rgbMatch) return null;

  const r = parseInt(rgbMatch[1]);
  const g = parseInt(rgbMatch[2]);
  const b = parseInt(rgbMatch[3]);

  // Convert to hex
  const toHex = (n: number) => {
    const hex = n.toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

/**
 * Converts ReactQuill class-based color to hex format
 * @param className - Class name like 'ql-color-red' or 'ql-color-A2E634'
 * @returns Hex color string or null if invalid
 */
const classColorToHex = (className: string): string | null => {
  // Handle hex colors in class names (ql-color-A2E634)
  const hexMatch = className.match(/ql-color-([A-F0-9]{6})/i);
  if (hexMatch) {
    return `#${hexMatch[1]}`;
  }

  // Handle named colors (ql-color-red)
  const namedColors: { [key: string]: string } = {
    red: '#FF0000',
    green: '#008000',
    blue: '#0000FF',
    yellow: '#FFFF00',
    orange: '#FFA500',
    purple: '#800080',
    black: '#000000',
    white: '#FFFFFF',
    gray: '#808080',
    grey: '#808080',
    // Additional colors for the new palette
    '111827': '#111827',
    '42b883': '#42b883',
    '2563eb': '#2563eb',
    eb2632: '#eb2632',
    ff9100: '#ff9100',
    '8b5cf6': '#8b5cf6',
    '06b6d4': '#06b6d4',
    ffc93c: '#ffc93c',
  };

  const namedMatch = className.match(/ql-color-(\w+)/);
  if (namedMatch && namedColors[namedMatch[1]]) {
    return namedColors[namedMatch[1]];
  }

  return null;
};

/**
 * Extracts color value from various formats (hex, rgb, class)
 * @param colorValue - Color value in any supported format
 * @returns Hex color string or null if invalid
 */
const extractColorValue = (colorValue: string): string | null => {
  // Already hex format
  if (colorValue.match(/^#[0-9a-fA-F]{3,8}$/)) {
    return colorValue.toLowerCase();
  }

  // RGB format
  if (colorValue.includes('rgb')) {
    return rgbToHex(colorValue);
  }

  // Class format
  if (colorValue.includes('ql-color-')) {
    return classColorToHex(colorValue);
  }

  const namedColors: { [key: string]: string } = {
    black: '#000000',
    red: '#FF0000',
    orange: '#FFA500',
    yellow: '#FFFF00',
    green: '#008000',
    blue: '#0000FF',
    purple: '#800080',
    white: '#FFFFFF',
    '111827': '#111827',
    '42b883': '#42b883',
    '2563eb': '#2563eb',
    eb2632: '#eb2632',
    ff9100: '#ff9100',
    '8b5cf6': '#8b5cf6',
    '06b6d4': '#06b6d4',
    ffc93c: '#ffc93c',
  };

  return namedColors[colorValue.toLowerCase()] || null;
};

/**
 * Converts ReactQuill HTML to Unity TextMeshPro compatible format
 *
 * This function processes HTML content from ReactQuill editor and converts it to
 * Unity TextMeshPro tags that can be rendered in the 3D environment.
 *
 * Key conversions:
 * - <strong> tags with styles → <b> with nested <color> and <size> tags
 * - <em> tags with styles → <i> with nested <color> and <size> tags
 * - <span> tags with styles → <color> and <size> tags
 * - Font sizes: px values → pt values (Unity TMP uses pt, conversion: pt = px * 2)
 * - Colors: CSS color values → Unity TMP color format
 * - Line breaks: <p> and <div> tags → <br> tags
 *
 * Note: Background colors are not supported in Unity TMP and are stripped out
 *
 * @param htmlContent - The HTML content from ReactQuill editor
 * @returns Unity TextMeshPro formatted string
 */
export const htmlToTmp = (htmlContent: string): string => {
  const sanitizedMessage = DOMPurify.sanitize(htmlContent);

  return (
    sanitizedMessage
      // Convert <strong> tags with inline styles to Unity TMP format
      .replace(/<strong[^>]*>(.*?)<\/strong>/g, (match, content) => {
        // Extract styles from the strong tag
        const styleMatch = match.match(/style="([^"]*)"/);
        if (styleMatch) {
          const styleStr = styleMatch[1];
          let out = content;

          // Process color styles (handle RGB, hex, and named colors)
          const colorMatch = styleStr.match(/(?:^|;)\s*color:\s*([^;]+)/i);
          if (colorMatch) {
            const colorValue = extractColorValue(colorMatch[1].trim());
            if (colorValue) {
              out = `<color=${colorValue}>${out}</color>`;
            }
          }

          // Process font size styles (convert px to pt for Unity TMP)
          const sizeMatch = styleStr.match(
            /(?:^|;)\s*font-size:\s*(8px|12px|16px|24px|32px)\b/i
          );
          if (sizeMatch) {
            const pxValue = sizeMatch[1];
            const ptValue = Math.round(parseInt(pxValue) * 2);
            out = `<size=${ptValue}pt>${out}</size>`;
          }

          return `<b>${out}</b>`;
        }
        return `<b>${content}</b>`;
      })
      // Convert <em> tags with inline styles to Unity TMP format
      .replace(/<em[^>]*>(.*?)<\/em>/g, (match, content) => {
        // Extract styles from the em tag
        const styleMatch = match.match(/style="([^"]*)"/);
        if (styleMatch) {
          const styleStr = styleMatch[1];
          let out = content;

          // Process color styles (handle RGB, hex, and named colors)
          const colorMatch = styleStr.match(/(?:^|;)\s*color:\s*([^;]+)/i);
          if (colorMatch) {
            const colorValue = extractColorValue(colorMatch[1].trim());
            if (colorValue) {
              out = `<color=${colorValue}>${out}</color>`;
            }
          }

          // Process font size styles (convert px to pt for Unity TMP)
          const sizeMatch = styleStr.match(
            /(?:^|;)\s*font-size:\s*(8px|12px|16px|24px|32px)\b/i
          );
          if (sizeMatch) {
            const pxValue = sizeMatch[1];
            const ptValue = Math.round(parseInt(pxValue) * 2);
            out = `<size=${ptValue}pt>${out}</size>`;
          }

          return `<i>${out}</i>`;
        }
        return `<i>${content}</i>`;
      })
      // Convert <span> tags with inline styles to Unity TMP format
      .replace(
        /<span style="([^"]*)">([\s\S]*?)<\/span>/g,
        (_m, styleStr, innerText) => {
          const colorMatch = styleStr.match(/(?:^|;)\s*color:\s*([^;]+)/i);
          const sizeMatch = styleStr.match(
            /(?:^|;)\s*font-size:\s*(8px|12px|16px|24px|32px)\b/i
          );
          let out = innerText;

          // Process font size (convert px to pt for Unity TMP)
          if (sizeMatch) {
            const pxValue = sizeMatch[1];
            const ptValue = Math.round(parseInt(pxValue) * 2);
            out = `<size=${ptValue}pt>${out}</size>`;
          }

          // Process color (handle RGB, hex, and named colors)
          if (colorMatch) {
            const colorValue = extractColorValue(colorMatch[1].trim());
            if (colorValue) {
              out = `<color=${colorValue}>${out}</color>`;
            }
          }

          return out;
        }
      )
      // Convert <span> tags with class-based colors to Unity TMP format
      .replace(
        /<span class="([^"]*)">([\s\S]*?)<\/span>/g,
        (_m, classStr, innerText) => {
          const colorClassMatch = classStr.match(/ql-color-[\w-]+/);
          const sizeClassMatch = classStr.match(/ql-size-(\w+)/);
          let out = innerText;

          // Process font size from class
          if (sizeClassMatch) {
            const sizeMap: { [key: string]: string } = {
              small: '16pt',
              large: '32pt',
              huge: '48pt',
            };
            const sizeValue = sizeMap[sizeClassMatch[1]];
            if (sizeValue) {
              out = `<size=${sizeValue}>${out}</size>`;
            }
          }

          // Process color from class
          if (colorClassMatch) {
            const colorValue = classColorToHex(colorClassMatch[0]);
            if (colorValue) {
              out = `<color=${colorValue}>${out}</color>`;
            }
          }

          return out;
        }
      )
      // Convert paragraph and div tags to line breaks
      .replace(/<p><br><\/p>/g, '<br><br>') // Convert double line breaks to <br><br>
      .replace(/<p>(.*?)<\/p>/g, '$1<br>') // Convert single line breaks to <br>
      .replace(/<div><br><\/div>/g, '<br><br>') // Convert div line breaks
      .replace(/<div>(.*?)<\/div>/g, '$1<br>') // Convert divs to line breaks
      .replace(/<span[^>]*>(.*?)<\/span>/g, '$1') // Remove any remaining span tags
      .replace(/<p[^>]*>(.*?)<\/p>/g, '$1<br>') // Convert any remaining p tags
      .replace(/<br><br><br>/g, '<br><br>') // Clean up excessive line breaks
      .replace(/^<br>/, '') // Remove leading line break
      .replace(/<br>$/, '')
  ); // Remove trailing line break
};

/**
 * Converts Unity TextMeshPro format to HTML for web preview rendering
 *
 * This function takes Unity TextMeshPro formatted content and converts it back
 * to HTML that can be safely rendered in the web browser for preview purposes.
 *
 * Key conversions:
 * - <b> tags → <strong> tags
 * - <i> tags → <em> tags
 * - <color> tags → <span style="color: ..."> tags
 * - <size> tags → <span style="font-size: ..."> tags (pt → px conversion)
 * - Line breaks: <br> tags remain as <br> tags
 *
 * Note: This is used only for preview rendering on the web side.
 * The actual Unity rendering uses the TMP format directly.
 *
 * @param tmpContent - The Unity TextMeshPro formatted content
 * @returns HTML string safe for web rendering
 */
export const tmpToHtml = (tmpContent: string): string => {
  if (!tmpContent) return '';

  return (
    tmpContent
      // Convert Unity TMP bold tags to HTML strong tags
      .replace(/<b>([\s\S]*?)<\/b>/g, '<strong>$1</strong>')
      // Convert Unity TMP italic tags to HTML em tags
      .replace(/<i>([\s\S]*?)<\/i>/g, '<em>$1</em>')
      // Convert Unity TMP color tags with named colors to HTML span tags
      .replace(
        /<color=\"(black|red|orange|yellow|green|blue|purple|white)\">([\s\S]*?)<\/color>/gi,
        '<span style="color: $1">$2</span>'
      )
      // Convert Unity TMP color tags with hex colors to HTML span tags
      .replace(
        /<color=(#[0-9a-fA-F]{3,8})>([\s\S]*?)<\/color>/g,
        '<span style="color: $1">$2</span>'
      )
      // Convert Unity TMP color tags with quoted hex colors to HTML span tags
      .replace(
        /<color="(#[0-9a-fA-F]{3,8})">([\s\S]*?)<\/color>/g,
        '<span style="color: $1">$2</span>'
      )
      // Convert Unity TMP color tags with quoted named colors to HTML span tags
      .replace(
        /<color="(black|red|orange|yellow|green|blue|purple|white)">([\s\S]*?)<\/color>/gi,
        '<span style="color: $1">$2</span>'
      )
      // Convert Unity TMP size tags to HTML span tags (pt → px conversion)
      .replace(
        /<size=(\d+pt|\d+px|\d+%|[+\-]?\d+)>([\s\S]*?)<\/size>/g,
        (_match, sizeValue, content) => {
          // Convert pt to px for web display (inverse of save conversion: px = pt / 2)
          if (sizeValue.includes('pt')) {
            const ptValue = parseInt(sizeValue);
            const pxValue = Math.round(ptValue / 2);
            return `<span style="font-size: ${pxValue}px">${content}</span>`;
          }
          return `<span style="font-size: ${sizeValue}">${content}</span>`;
        }
      )
      // Normalize line break tags
      .replace(/<br\s*\/>/g, '<br>')
      .replace(/<br>/g, '<br>')
  );
};
